import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { HttpStatusCode } from '@angular/common/http';
import type { OnInit, Signal } from '@angular/core';
import { Component, Injector, effect, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import type { NonNullableFormBuilder } from '@angular/forms';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import type {
  OnyxDate,
  OnyxHttpErrorResponse,
  OnyxSuggestion,
} from '@onyx/angular';
import {
  FormHelper,
  OnyxDatepickerComponent,
  OnyxDropdownComponent,
  OnyxFormGroupComponent,
  OnyxSectionComponent,
  OnyxSuggestionsComponent,
  OnyxTextFieldComponent,
  OnyxToastService,
  OnyxUploadComponent,
  onyxDateValidator,
  onyxMaxNumberValidator,
  onyxMinNumberValidator,
  onyxNipNumberValidator,
  onyxTextFieldLengthValidator,
} from '@onyx/angular';
import type { Country } from '../../../../common/enums/country';
import { DictionaryCode } from '../../../../common/enums/dictionary-code';
import { ValidationHelper } from '../../../../common/helpers/validation.helper';
import { DataProvidersService } from '../../../../common/services/data-providers.service';
import { DictionariesService } from '../../../../common/services/dictionaries.service';
import type { PreferencesService } from '../../../../common/services/preferences.service';
import { ContractType } from '../../common/enums/contract-type';
import { DriverSettlementMethod } from '../../common/enums/driver-settlement-method';

type DriverEmploymentConditionsFormGroup = ReturnType<
  typeof DriverEmploymentConditionsFormComponent.buildForm
>;

@Component({
  selector: 'app-driver-employment-conditions-form',
  imports: [
    OnyxSectionComponent,
    ReactiveFormsModule,
    OnyxDropdownComponent,
    AsyncPipe,
    OnyxDatepickerComponent,
    OnyxUploadComponent,
    OnyxTextFieldComponent,
    OnyxFormGroupComponent,
    NgTemplateOutlet,
    OnyxSuggestionsComponent,
  ],
  templateUrl: './driver-employment-conditions-form.component.html',
  styleUrl: './driver-employment-conditions-form.component.scss',
})
export class DriverEmploymentConditionsFormComponent implements OnInit {
  private readonly dictionariesService = inject(DictionariesService);
  private readonly injector = inject(Injector);
  private readonly dataProvidersService = inject(DataProvidersService);
  private readonly toastService = inject(OnyxToastService);

  public readonly form = input.required<DriverEmploymentConditionsFormGroup>();
  public readonly nationality = input.required<Country | null>();

  protected readonly I18N = 'drivers.driverForm.employmentConditions';
  protected readonly CONTRACT_TYPE_SUGGESTIONS: OnyxSuggestion<ContractType>[] =
    [
      {
        name: `${DictionaryCode.CONTRACT_TYPE}.${ContractType.B2B}`,
        value: ContractType.B2B,
      },
      {
        name: `${DictionaryCode.CONTRACT_TYPE}.${ContractType.EMPLOYMENT_CONTRACT}`,
        value: ContractType.EMPLOYMENT_CONTRACT,
      },
    ];

  protected readonly ContractType = ContractType;
  protected readonly DriverSettlementMethod = DriverSettlementMethod;

  protected readonly contractTypes$ = this.dictionariesService.getDictionary(
    DictionaryCode.CONTRACT_TYPE,
  );
  protected readonly settlementMethods$ =
    this.dictionariesService.getDictionary(
      DictionaryCode.DRIVER_SETTLEMENT_METHOD,
    );
  protected readonly currencies$ = this.dictionariesService.getDictionary(
    DictionaryCode.CURRENCY,
  );

  protected contractType!: Signal<ContractType | null>;
  protected settlementMethod!: Signal<DriverSettlementMethod>;

  public ngOnInit(): void {
    const controls = this.form().controls;

    const contractTypeControl = controls.contractType;
    this.contractType = toSignal(contractTypeControl.valueChanges, {
      initialValue: contractTypeControl.value,
      injector: this.injector,
    });

    const settlementMethodControl = controls.settlementMethod;
    this.settlementMethod = toSignal(settlementMethodControl.valueChanges, {
      initialValue: settlementMethodControl.value,
      injector: this.injector,
    });

    const dailyRateControl = controls.dailyRate;
    const mileageRateControl = controls.mileageRate;
    const baseRateControl = controls.baseRate;
    const freightPercentageControl = controls.freightPercentage;
    const roundTripRateControl = controls.roundTripRate;

    effect(
      () => {
        const settlementMethod = this.settlementMethod();
        ValidationHelper.toggleControls(dailyRateControl, {
          enabled: settlementMethod === DriverSettlementMethod.DAILY_RATE,
          reset: true,
        });
        ValidationHelper.toggleControls(mileageRateControl, {
          enabled: settlementMethod === DriverSettlementMethod.MILEAGE_RATE,
          reset: true,
        });
        ValidationHelper.toggleControls(
          [baseRateControl, freightPercentageControl],
          {
            enabled:
              settlementMethod === DriverSettlementMethod.BASE_PLUS_PERCENTAGE,
            reset: true,
          },
        );
        ValidationHelper.toggleControls(roundTripRateControl, {
          enabled: settlementMethod === DriverSettlementMethod.ROUND_TRIP_RATE,
          reset: true,
        });
      },
      { injector: this.injector },
    );

    const nipNumberControl = controls.nipNumber;
    const nipNumber = toSignal(nipNumberControl.valueChanges, {
      initialValue: nipNumberControl.value,
      injector: this.injector,
    });
    const companyNameControl = controls.companyName;

    effect(
      () => {
        const contractType = this.contractType();
        if (contractType === ContractType.B2B) {
          nipNumberControl.addValidators(Validators.required);
          companyNameControl.addValidators(Validators.required);
        } else {
          nipNumberControl.removeValidators(Validators.required);
          companyNameControl.removeValidators(Validators.required);
          FormHelper.reset(nipNumberControl);
          FormHelper.reset(companyNameControl);
        }
      },
      { injector: this.injector },
    );

    effect(
      () => {
        const nationality = this.nationality();
        if (!nationality) return;

        const contractType = this.contractType();
        if (contractType !== ContractType.B2B) return;

        nipNumber();
        nipNumberControl.updateValueAndValidity();
        companyNameControl.updateValueAndValidity();

        if (nipNumberControl.invalid) {
          FormHelper.reset(companyNameControl);
          return;
        } else if (companyNameControl.value) {
          return;
        }

        this.dataProvidersService
          .getCompanyData(nationality, nipNumber()!)
          .subscribe({
            next: (companyData) => {
              this.toastService.showSuccess({
                message: `${this.I18N}.downloadedFromGusApi`,
              });
              companyNameControl.setValue(companyData.companyName);
            },
            error: (response: OnyxHttpErrorResponse) => {
              if (response.status === HttpStatusCode.NotFound) {
                this.toastService.showError({
                  message: `${this.I18N}.notFoundInRegistry`,
                });
                response.handled = true;
              }

              throw response;
            },
          });
      },
      { injector: this.injector },
    );
  }

  public static buildForm(
    fb: NonNullableFormBuilder,
    preferencesService: PreferencesService,
  ) {
    return fb.group({
      contractType: fb.control<ContractType | null>(null, [
        Validators.required,
      ]),
      settlementMethod: fb.control<DriverSettlementMethod>(
        DriverSettlementMethod.DAILY_RATE,
        [Validators.required],
      ),

      // DriverSettlementMethod.DAILY_RATE:
      dailyRate: FormHelper.buildAmountForm(fb, preferencesService, {
        minValue: 0,
      }),
      // DriverSettlementMethod.MILEAGE_RATE:
      mileageRate: FormHelper.buildAmountForm(fb, preferencesService, {
        minValue: 0,
      }),
      // DriverSettlementMethod.BASE_PLUS_PERCENTAGE:
      baseRate: FormHelper.buildAmountForm(fb, preferencesService, {
        minValue: 0,
      }),
      freightPercentage: fb.control<number | null>(null, [
        Validators.required,
        onyxMinNumberValidator(0, 1),
        onyxMaxNumberValidator(100, 1),
      ]),
      // DriverSettlementMethod.ROUND_TRIP_RATE:
      roundTripRate: FormHelper.buildAmountForm(fb, preferencesService, {
        minValue: 0,
      }),

      contractTerm: fb.control<OnyxDate | null>(null, [
        Validators.required,
        onyxDateValidator,
      ]),
      scans: fb.control<string[]>([]),

      nipNumber: fb.control<string | null>(null, [onyxNipNumberValidator]),
      companyName: fb.control<string | null>({ value: null, disabled: true }, [
        onyxTextFieldLengthValidator,
      ]),
    });
  }
}
